import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ADD_TO_CART_TOAST_DURATION } from "../../lib/toastConfig";
import { useFavorites } from "../../contexts/FavoritesContext";
import { useCart } from "../../contexts/CartContext";
import { webpVariant } from "../../lib/optimizedImage";
import ProductPrice from "./ProductPrice";
import { getProductDisplayName } from "../../utils/productDisplay";
import { getProductPath } from "../../utils/productLinks";

const getProductCaptionBody = (product) =>
  product.description ||
  product.category ||
  product.mainCategories ||
  "";

const isVideoSrc = (src) =>
  typeof src === "string" && /\.(mp4|mov|webm|m4v)$/i.test(src);

const getProductPrimaryImage = (product) => {
  const rawImages = (
    product.images && product.images.length > 0
      ? product.images
      : [product.image]
  ).filter(Boolean);

  return rawImages.find((src) => !isVideoSrc(src)) || product.image;
};

const ShopProductGrid = ({ products }) => {
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToCart } = useCart();

  return (
    <div className="grid grid-cols-3 gap-x-2 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
      {products.map((product) => {
        const src = getProductPrimaryImage(product);
        if (!src) return null;

        const displayName = getProductDisplayName(product);
        const favorited = isFavorite(product.id);
        const cellWebp480 = webpVariant(src, 480);
        const cellWebp960 = webpVariant(src, 960);
        const cellWebpFull = webpVariant(src);
        const cellWebpSrcSet = cellWebp960
          ? `${cellWebp480 ?? cellWebp960} 480w, ${cellWebp960} 960w${
              cellWebpFull ? `, ${cellWebpFull} 1600w` : ""
            }`
          : "";

        const captionBody = getProductCaptionBody(product);

        return (
          <div
            key={product.id}
            className="group/cell flex flex-col items-stretch"
          >
            <div className="relative aspect-square w-full overflow-hidden rounded-md border-2 border-gray-200 transition-all duration-200 group-hover/cell:border-amber-300">
              <button
                type="button"
                onClick={() => navigate(getProductPath(product))}
                aria-label={`View ${displayName}`}
                className="absolute inset-0 block h-full w-full cursor-pointer"
              >
                <picture className="absolute inset-0 block h-full w-full">
                  {cellWebp960 && (
                    <source
                      type="image/webp"
                      srcSet={cellWebpSrcSet}
                      sizes="(min-width: 1024px) 160px, (min-width: 640px) 20vw, 30vw"
                    />
                  )}
                  <img
                    src={src}
                    alt={displayName}
                    loading="lazy"
                    decoding="async"
                    className="block h-full w-full object-cover"
                  />
                </picture>
              </button>
              <button
                type="button"
                onClick={() => toggleFavorite(product)}
                aria-label={
                  favorited
                    ? `Remove ${displayName} from wishlist`
                    : `Save ${displayName} to wishlist`
                }
                title={favorited ? "Remove from wishlist" : "Save to wishlist"}
                className={`absolute top-1 left-1 z-10 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm transition-all duration-200 ${
                  favorited
                    ? "border-red-200 bg-white text-red-500"
                    : "border-white/70 bg-white/90 text-gray-500 hover:text-red-500"
                }`}
              >
                <svg
                  className="h-2.5 w-2.5"
                  fill={favorited ? "currentColor" : "none"}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => {
                  addToCart(product);
                  toast.success(`${displayName} added to cart`, {
                    duration: ADD_TO_CART_TOAST_DURATION,
                  });
                }}
                aria-label={`Add ${displayName} to cart`}
                className="absolute bottom-1.5 right-1.5 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 p-0 text-gray-950 opacity-100 shadow-md transition-all duration-200 hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-white sm:bottom-2 sm:left-1/2 sm:right-auto sm:h-auto sm:w-auto sm:-translate-x-1/2 sm:translate-y-2 sm:gap-1 sm:whitespace-nowrap sm:rounded-md sm:px-2.5 sm:py-1.5 sm:text-[9px] sm:font-bold sm:uppercase sm:tracking-wide sm:opacity-0 sm:shadow-lg sm:group-hover/cell:translate-y-0 sm:group-hover/cell:opacity-100 sm:group-focus-within/cell:translate-y-0 sm:group-focus-within/cell:opacity-100"
              >
                <svg
                  className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.25}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13 5.4 5M7 13l-2 4h13M9 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                  />
                </svg>
                <span className="hidden sm:inline">Add to Cart</span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate(getProductPath(product))}
              className="mt-0.5 flex flex-col items-stretch gap-0.5 text-left cursor-pointer"
            >
              <p className="text-xs sm:text-sm leading-tight font-semibold line-clamp-1 text-center text-white transition-colors duration-200 group-hover/cell:text-amber-200">
                {displayName}
              </p>
              <div className="flex justify-center">
                <ProductPrice
                  product={product}
                  size="grid"
                  stacked
                  className="justify-center [&_.line-through]:text-slate-400 [&_.font-bold]:!text-amber-400"
                />
              </div>
              {captionBody && (
                <p className="text-[10px] sm:text-xs leading-snug font-normal line-clamp-2 text-center text-slate-300 transition-colors duration-200 group-hover/cell:text-slate-200">
                  {captionBody}
                </p>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ShopProductGrid;
